import React, { useEffect, useMemo, useState } from "react";
import { useStores } from "../../../use-stores";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Container,
  Grid2,
  Paper,
  Typography,
} from "@mui/material";
import CustomTypography from "../../components/common/form/CustomTypography";
import AppButton from "../../components/common/buttons/AppButton";
import CustomTextField from "../../components/common/textFields/TextField";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { observer } from "mobx-react";
import { useNotification } from "../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import UserTable from "../../components/user/UserTable";
import DeleteConfirmation from "../../components/common/pop-up/DeleteConfirmation";
import { UserFilters } from "../../../models/User/UserFilters";
import { SortType } from "../../../models/SortType";
import FilterSidebar from "../../components/common/filter/FilterSidebar";
import { FilterSidebarMenu, FilterMenuType } from "../../../models/FilterMenu";
import { ModuleType } from "../../../models/ModuleType";
import { FilterValue } from "../../../models/Filters/FilterValue";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";

const USER_STATUS_OPTIONS: FilterValue[] = [
  { id: "true", value: "Active" },
  { id: "false", value: "Inactive" },
];

interface UserSelectedFilters {
  active: boolean;
}

const UserListing: React.FC = () => {
  const { userStore } = useStores();

  const { showError, showSuccess } = useNotification();

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [openFilter, setOpenFilter] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialParams: UserFilters = {
    page_no: searchParams.get("page")
      ? parseInt(searchParams.get("page") as string, 10)
      : 1,
    search_text: searchParams.get("search_text") || undefined,
    active: searchParams.get("active")
      ? searchParams.get("active") === "false"
        ? false
        : true
      : true,
    limit: searchParams.get("limit")
      ? parseInt(searchParams.get("limit") as string, 10)
      : 30,
    sort_by: searchParams.get("sort_by") || "created_date",
    sort_type:
      searchParams.get("sort_type") === "ASC" ? SortType.ASC : SortType.DESC,
  };

  const initialFilters: UserSelectedFilters = {
    active: searchParams.get("active")
      ? searchParams.get("active") === "false"
        ? false
        : true
      : true,
  };

  const [filters, setFilters] = useState<UserSelectedFilters>(initialFilters);
  const [params, setParams] = useState(initialParams);

  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search_text") || ""
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    userStore
      .getUsers(params)
      .catch((err) =>
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later."
        )
      )
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    const updateQueryParams = () => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (
          value !== undefined &&
          (!Array.isArray(value) || value.length > 0)
        ) {
          const paramValue = Array.isArray(value)
            ? value.join(",")
            : value.toString();
          searchParams.set(key, paramValue);
        }
      });

      const queryString = searchParams.toString();
      if (queryString) {
        navigate({ search: queryString }, { replace: true });
      }
    };

    updateQueryParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, navigate]);

  const handleSearch = (query: string) => {
    if (query.length !== 0) {
      setSearchTerm(query);
    } else {
      setSearchTerm("");
      setParams((prev) => ({
        ...prev,
        search_text: "",
      }));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (searchTerm.trim().length > 0) {
        setParams((prev) => ({
          ...prev,
          search_text: searchTerm,
          page_no: 1,
        }));
      } else {
        setSearchTerm("");
        setParams((prev) => ({
          ...prev,
          search_text: "",
          page_no: 1,
        }));
      }
    }
  };

  const handleApply = () => {
    setParams((prev) => ({
      ...prev,
      active: filters.active,
    }));
  };

  const resetFilters = () => {
    setSearchTerm("");
    setFilters({
      active: true,
    });
    setParams({
      page_no: 1,
      limit: 30,
      sort_by: "created_date",
      sort_type: SortType.DESC,
      active: true,
    });
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setParams((prev) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  const handleDeleteConfirm = async (userID: number) => {
    setIsLoading(true);
    if (userID) {
      const res = await userStore.deleteUser(userID);
      setDeleteId(null);
      if (res?.success) {
        showSuccess(res?.result);
        userStore
          .getUsers(params)
          .catch((err) =>
            showError(
              "Error",
              err?.response?.data?.message ||
                "Something went wrong! Please try again later."
            )
          )
          .finally(() => setIsLoading(false));
      } else {
        setIsLoading(false);
        showError("Error Occurred", "Error deleting user");
      }
    }
  };

  const filterMenu: FilterSidebarMenu[] = useMemo(
    () => [
      {
        moduleType: ModuleType.USER,
        fieldType: FilterMenuType.LOCAL_SINGLE_SELECT,
        label: "Status",
        placeholder: "Select status",
        list: USER_STATUS_OPTIONS,
        value: filters.active
          ? [{ id: "true", value: "Active" }]
          : [{ id: "false", value: "Inactive" }],
        onChange: (selected: FilterValue[]) => {
          const id = selected[0]?.id;
          setFilters((prev) => ({
            ...prev,
            active: id !== "false",
          }));
        },
      },
    ],
    [filters.active]
  );

  const handleFilterToggle = () => {
    setOpenFilter(!openFilter);
  };

  const handleApplyFilters = () => {
    handleApply();
    handleFilterToggle();
  };

  const handleClearFilters = () => {
    resetFilters();
    handleFilterToggle();
  };

  const hasUserSectionAccess =
    userStore.hasPermission(EmployeeRoles.VIEW_USER) ||
    userStore.hasPermission(EmployeeRoles.EDIT_USER) ||
    userStore.hasPermission(EmployeeRoles.CREATE_USER);

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      <Paper>
        {!hasUserSectionAccess ? (
          <PermissionBlockLabel />
        ) : (
          <Box
            sx={{
              borderRadius: "12px",
              border: "1px solid #E2E8F0",
              bgcolor: "#FFFFFF",
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              paddingX: 3,
              paddingY: 2,
              minHeight: { xs: "60vh", md: window.innerHeight - 110 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <CustomTypography variant="heading3" fontWeight={700}>
                Users
              </CustomTypography>
              {userStore.hasPermission(EmployeeRoles.CREATE_USER) && (
                <AppButton
                  startIcon={<AddOutlinedIcon />}
                  variant="primary"
                  label="Add User"
                  onClick={() => navigate("/users/add-edit-user")}
                  customStyles={{ width: "140px" }}
                />
              )}
            </Box>

            <Grid2
              container
              spacing={2}
              columns={12}
              sx={{
                mt: 3,
                mb: 3,
                alignItems: "center",
                width: "100%",
              }}
            >
              <Grid2
                size={{ xs: 12, sm: 12, md: 11, lg: 11 }}
                sx={{ minWidth: 0 }}
              >
                <CustomTextField
                  placeholder="Search by user name or email"
                  value={searchTerm}
                  onSearch={(query) => handleSearch(query)}
                  onKeyDown={handleKeyDown}
                  width={"100%"}
                />
              </Grid2>
              <Grid2
                size={{ xs: 12, sm: 12, md: 1, lg: 1 }}
                sx={{
                  display: "flex",
                  justifyContent: { xs: "flex-start", md: "flex-end" },
                }}
              >
                <AppButton
                  variant="grayscale"
                  label="Filter"
                  startIcon={<FilterAltOutlinedIcon />}
                  onClick={() => handleFilterToggle()}
                  customStyles={{ width: "125px", flexShrink: 0 }}
                />
              </Grid2>
            </Grid2>

            {isLoading && (
              <Container
                sx={{
                  minHeight: "60vh",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress />
              </Container>
            )}

            {!isLoading && userStore.users.length <= 0 && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="10rem"
                textAlign="center"
              >
                <Typography
                  variant="h6"
                  fontWeight="500"
                  color="text.secondary"
                >
                  No data found
                </Typography>
              </Box>
            )}

            {!isLoading && userStore.users.length > 0 && (
              <UserTable
                maxHeight={window.innerHeight - 340}
                users={userStore.users}
                limit={params.limit || userStore.limit}
                page_no={params.page_no}
                total_count={userStore.total_count}
                total_pages={userStore.total_pages}
                sortBy={params.sort_by}
                sortType={params.sort_type}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                onDelete={(id) => setDeleteId(id)}
                onSortChange={(column, sortType) => {
                  setParams((prev) => ({
                    ...prev,
                    sort_by: column,
                    sort_type: sortType,
                  }));
                }}
              />
            )}

            {deleteId !== null && (
              <DeleteConfirmation
                id={deleteId}
                open={true}
                onDeleteConfirm={() => handleDeleteConfirm(deleteId)}
                onCancel={() => setDeleteId(null)}
                title="Confirm Deletion"
                content="Are you sure you want to delete this user?"
              />
            )}

            {openFilter && (
              <FilterSidebar
                isOpen={openFilter}
                items={filterMenu}
                onApply={handleApplyFilters}
                handleClear={handleClearFilters}
                toggleFilter={handleFilterToggle}
              />
            )}
          </Box>
        )}
      </Paper>
    </Grid2>
  );
};

export default observer(UserListing);
